import * as tx from '@staiter/i18n';
/**
 * FM-S-multi-4-14 — /admin/entities/$entityId detail с tabs: Info / Users / TenantConfig.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronRight, Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { emitToast } from '~/components/feedback';
import { Button, ConfirmDialog, Input, Select } from '~/components/primitives';
import {
  useEntity,
  useEntityUsers,
  useHoldings,
  useRemoveEntityMembership,
  useTenantConfig,
  useUpdateEntity,
  useUpdateEntityMembership,
  useUpdateTenantConfig,
} from '~/lib/api/tenancy';
import { ENTITY_MEMBERSHIP_ROLES, getRoleLabel } from '~/lib/rbac';
import { useIsSuperAdmin } from '~/lib/rbac/use-tenancy-rbac';

/** Роль derived-через-холдинг менять на уровне компании нельзя — показываем read-only. */
const ROLE_OPTIONS = ENTITY_MEMBERSHIP_ROLES.map((r) => ({ value: r, label: getRoleLabel(r) }));

export const Route = createFileRoute('/_auth/admin/entities_/$entityId')({
  component: EntityDetail,
});

type Tab = 'info' | 'users' | 'config';

function EntityDetail(): JSX.Element {
  const { entityId } = Route.useParams();
  const [tab, setTab] = useState<Tab>('info');
  const detail = useEntity(entityId);
  const usersQ = useEntityUsers(entityId);
  const tcQ = useTenantConfig(entityId);

  if (detail.isLoading) return <p>{tx.ui_zagruzka()}</p>;
  if (!detail.data) return <p>{tx.ui_kompaniya_ne_naydena()}</p>;

  const { entity, holding } = detail.data;

  return (
    <div>
      <PageHeader
        title={entity.name}
        description={holding ? `Холдинг: ${holding.name}` : 'Standalone (без холдинга)'}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button variant={tab === 'info' ? 'primary' : 'ghost'} onClick={() => setTab('info')}>
          {tx.ui_informaciya()}
        </Button>
        <Button variant={tab === 'users' ? 'primary' : 'ghost'} onClick={() => setTab('users')}>
          {tx.ui_polzovateli()}
        </Button>
        <Button variant={tab === 'config' ? 'primary' : 'ghost'} onClick={() => setTab('config')}>
          {tx.ui_nastroyki()}
        </Button>
      </div>

      {tab === 'info' && <InfoTab entityId={entityId} />}

      {tab === 'users' && (
        <Table
          ariaLabel="Пользователи компании"
          data={(usersQ.data?.items ?? []) as Array<Record<string, unknown>>}
          rowKey={(u) => u.user_id as string}
          columns={[
            { id: 'email', header: 'Email', accessor: (u) => u.email as string },
            { id: 'name', header: 'Имя', accessor: (u) => (u.full_name as string) ?? '—' },
            {
              id: 'source',
              header: 'Источник',
              accessor: (u) => <SourceChip source={u.source as string} />,
            },
            {
              id: 'role',
              header: 'Роль',
              accessor: (u) => (
                <EntityRoleCell
                  entityId={entityId}
                  userId={u.user_id as string}
                  role={u.effective_role as string}
                  source={u.source as string}
                  holding={holding}
                />
              ),
            },
            {
              id: 'actions',
              header: '',
              accessor: (u) => (
                <EntityRemoveCell
                  entityId={entityId}
                  userId={u.user_id as string}
                  source={u.source as string}
                />
              ),
            },
          ]}
        />
      )}

      {tab === 'config' && tcQ.data && <TenantConfigTab entityId={entityId} config={tcQ.data} />}
    </div>
  );
}

/** Источник доступа: прямой (редактируемый здесь) vs через холдинг (derived, read-only). */
function SourceChip({ source }: { source: string }): JSX.Element {
  const direct = source === 'direct';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-sm, 6px)',
        fontSize: '0.75rem',
        fontWeight: 500,
        background: direct
          ? 'var(--surface-sunken, rgba(127,127,127,0.12))'
          : 'var(--accent-bg-15, rgba(64,120,255,0.15))',
        color: direct ? 'var(--text-secondary, inherit)' : 'var(--accent-strong, inherit)',
      }}
    >
      {direct ? 'Прямой' : 'Через холдинг'}
    </span>
  );
}

/**
 * Read-only роль (derived-доступ) — muted-бейдж с замком: «так задумано», не «не догрузилось».
 * FE-6 (tech-debt-carryover): пользователь не понимал, ГДЕ менять унаследованную роль. Теперь бейдж
 * несёт tooltip «Роль из холдинга — менять на уровне холдинга» и, если холдинг известен, кликабелен
 * → ведёт на страницу холдинга.
 */
function ReadOnlyRoleBadge({
  role,
  holding,
}: {
  role: string;
  holding?: { id: string; name: string } | null;
}): JSX.Element {
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 10px',
    borderRadius: 'var(--radius-pill, 999px)',
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
    background: 'var(--surface-sunken, rgba(127,127,127,0.10))',
    textDecoration: 'none',
  };
  const title = holding
    ? `Роль из холдинга «${holding.name}» — менять на уровне холдинга`
    : 'Роль унаследована через холдинг — менять на уровне холдинга';
  if (holding) {
    // FE-6 design-review (оба дизайнера, MINOR): замок читается как «нет действия», а элемент
    // кликабелен → добавляем видимый link-аффорданс в покое (underline + «→»), не только hover.
    return (
      <Link
        to="/admin/holdings/$holdingId"
        params={{ holdingId: holding.id }}
        title={title}
        aria-label={title}
        style={{
          ...badgeStyle,
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'var(--border-strong, currentColor)',
          textUnderlineOffset: 2,
        }}
      >
        <Lock size={12} aria-hidden /> {getRoleLabel(role)}
        <ChevronRight size={12} aria-hidden />
      </Link>
    );
  }
  return (
    <span title={title} style={badgeStyle}>
      <Lock size={12} aria-hidden /> {getRoleLabel(role)}
    </span>
  );
}

/** Danger-кнопка удаления в строке таблицы (иконка + danger-цвет, вес деструктива). */
function RemoveButton({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      style={{ color: 'var(--danger)' }}
      leadingIcon={<Trash2 size={14} />}
    >
      {label}
    </Button>
  );
}

/** Роль пользователя в компании: Select для `source=direct`, иначе read-only lock-бейдж. */
function EntityRoleCell({
  entityId,
  userId,
  role,
  source,
  holding,
}: {
  entityId: string;
  userId: string;
  role: string;
  source: string;
  holding?: { id: string; name: string } | null;
}): JSX.Element {
  const qc = useQueryClient();
  const update = useUpdateEntityMembership(userId);

  if (source !== 'direct') return <ReadOnlyRoleBadge role={role} holding={holding} />;

  const options = ROLE_OPTIONS.some((o) => o.value === role)
    ? ROLE_OPTIONS
    : [{ value: role, label: getRoleLabel(role) }, ...ROLE_OPTIONS];

  return (
    <div style={{ maxWidth: 200 }}>
      <Select
        label=""
        size="sm"
        value={role}
        disabled={update.isPending}
        onChange={async (e) => {
          try {
            await update.mutateAsync({
              entityId,
              input: { role: e.target.value, reason: 'Смена роли через карточку компании' },
            });
            await qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId, 'users'] });
            emitToast({ variant: 'success', title: 'Роль изменена' });
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось изменить роль' });
          }
        }}
        options={options}
      />
    </div>
  );
}

/** «Убрать» из компании — только для `source=direct`. */
function EntityRemoveCell({
  entityId,
  userId,
  source,
}: {
  entityId: string;
  userId: string;
  source: string;
}): JSX.Element | null {
  const qc = useQueryClient();
  const remove = useRemoveEntityMembership(userId);
  const [confirmRemove, setConfirmRemove] = useState(false);
  if (source !== 'direct') return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <RemoveButton label="Убрать" onClick={() => setConfirmRemove(true)} />
      <ConfirmDialog
        open={confirmRemove}
        variant="danger"
        title="Убрать из компании?"
        description="Пользователь потеряет прямой доступ к этой компании."
        confirmLabel="Убрать"
        onConfirm={async () => {
          try {
            await remove.mutateAsync({ entityId });
            await qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId, 'users'] });
            emitToast({ variant: 'success', title: 'Удалено' });
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось удалить' });
          }
          setConfirmRemove(false);
        }}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}

function InfoTab({ entityId }: { entityId: string }): JSX.Element {
  const detail = useEntity(entityId);
  const qc = useQueryClient();
  const isSuper = useIsSuperAdmin();
  const holdingsQ = useHoldings();
  const m = useUpdateEntity(entityId);
  const [name, setName] = useState(detail.data?.entity.name ?? '');
  const [bin, setBin] = useState(detail.data?.entity.bin ?? '');
  const [address, setAddress] = useState(
    (detail.data?.entity as { address?: string | null } | undefined)?.address ?? '',
  );

  if (!detail.data) return <></>;
  const currentHoldingId = detail.data.holding?.id ?? '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 500 }}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await m.mutateAsync({ name, bin: bin || null, address: address || null });
            emitToast({ variant: 'success', title: 'Сохранено' });
            await qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
          } catch {
            emitToast({ variant: 'error', title: 'Ошибка сохранения' });
          }
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <Input label="Название" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="БИН"
          value={bin}
          onChange={(e) => setBin(e.target.value)}
          placeholder="12 цифр"
        />
        <Input label="Адрес" value={address} onChange={(e) => setAddress(e.target.value)} />
        <Button type="submit" disabled={m.isPending} style={{ alignSelf: 'flex-start' }}>
          {tx.ui_sohranit()}
        </Button>
      </form>

      {/* Принадлежность к холдингу — менять может только super-admin (BE enforce). */}
      {isSuper && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Select
            label="Холдинг"
            value={currentHoldingId}
            disabled={m.isPending}
            onChange={async (e) => {
              const val = e.target.value || null;
              try {
                await m.mutateAsync({ holding_id: val });
                emitToast({ variant: 'success', title: 'Холдинг обновлён' });
                await qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
                await qc.invalidateQueries({ queryKey: ['admin', 'holdings'] });
              } catch {
                emitToast({ variant: 'error', title: 'Не удалось изменить холдинг' });
              }
            }}
            options={[
              { value: '', label: '— Без холдинга (standalone)' },
              ...(holdingsQ.data?.items ?? []).map((h) => ({ value: h.id, label: h.name })),
            ]}
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {tx.ui_smena_holdinga_vliyaet_na_dostup_polzovateley_unasledovan()}
          </span>
        </div>
      )}
    </div>
  );
}

function TenantConfigTab({
  entityId,
  config,
}: {
  entityId: string;
  config: {
    default_currency: string;
    fiscal_year_start_month: number;
    onboarding_date: string | null;
  };
}): JSX.Element {
  const [currency, setCurrency] = useState(config.default_currency);
  const [fyMonth, setFyMonth] = useState(config.fiscal_year_start_month);
  const [onboardingDate, setOnboardingDate] = useState(config.onboarding_date ?? '');
  const qc = useQueryClient();
  const m = useUpdateTenantConfig(entityId);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await m.mutateAsync({
            default_currency: currency as 'KZT' | 'USD' | 'EUR' | 'RUB',
            fiscal_year_start_month: fyMonth,
            onboarding_date: onboardingDate || null,
          });
          emitToast({ variant: 'success', title: 'Настройки сохранены' });
          await qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
        } catch {
          emitToast({ variant: 'error', title: 'Не удалось сохранить' });
        }
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 500 }}
    >
      <Select
        label="Валюта по умолчанию"
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        options={[
          { value: 'KZT', label: 'KZT' },
          { value: 'USD', label: 'USD' },
          { value: 'EUR', label: 'EUR' },
          { value: 'RUB', label: 'RUB' },
        ]}
      />
      <Input
        label="Начало финансового года (месяц 1–12)"
        type="number"
        min={1}
        max={12}
        value={String(fyMonth)}
        onChange={(e) => setFyMonth(Number(e.target.value))}
      />
      <Input
        label="Дата начала учёта (устанавливается один раз)"
        type="date"
        value={onboardingDate}
        onChange={(e) => setOnboardingDate(e.target.value)}
        disabled={!!config.onboarding_date}
        hint={config.onboarding_date ? 'Уже установлена, изменить нельзя' : ''}
      />
      <Button type="submit" disabled={m.isPending} style={{ alignSelf: 'flex-start' }}>
        {tx.ui_sohranit()}
      </Button>
    </form>
  );
}
